import type { ToolCatalogEntry, RequiredLevel } from './types';
import type { StoredToolAccess } from '../../types/backend';
import {
  GENERATED_TOOL_CATALOG,
  GENERATED_DOMAIN_LABELS,
  GENERATED_GOVERNANCE,
  GENERATED_GLOBAL_POLICIES,
  GENERATED_REQUIRED_LEVEL,
  GENERATED_WORKFLOW_TOOLS,
  GENERATED_KIND_CEILING,
  GENERATED_OUTSIDE_ACCESS_CHECK,
  GENERATED_FILTERED_LISTINGS,
  GENERATED_AWS_FOUR_USE,
  GENERATED_AWS_THREE_USE,
  GENERATED_JIT_SHIPPED,
  GENERATED_ACCESS_ACTIONS,
  GENERATED_DATASET_WRITE_FLOOR,
  GENERATED_AWS_USE_SHIPPED,
} from './generated/toolCatalog.generated';
import type { GeneratedToolAccess } from './generated/toolCatalog.generated';

/**
 * The tool catalog: the tools an agent can be built with, the same set the Studio's offer is generated
 * from, by backend/scripts/generate_tool_catalog.py. Code owns existence and signature; an admin's
 * description override and off switch are the ToolConfig overlay (toolset.UpsertToolConfig).
 */

/** Each entry with its access facts: the modes its authorization takes, and whether it escalates. */
export const TOOL_CATALOG: (ToolCatalogEntry & GeneratedToolAccess)[] = GENERATED_TOOL_CATALOG;

export const DOMAIN_LABELS: Record<string, string> = GENERATED_DOMAIN_LABELS;

/** action_id → the base_policies.cedar rules that govern that tool. */
export const GOVERNANCE = GENERATED_GOVERNANCE;

/** Broad walls/wildcards (JIT, super-admin and other cross-cutting rules) that apply across all tools. */
export const GLOBAL_POLICIES = GENERATED_GLOBAL_POLICIES;

/**
 * Every registry's tools with their access facts: the set a workflow publish is checked against
 * (agent_tool_access), so the workflow step editor offers from it, not from the agent catalog.
 */
export const WORKFLOW_TOOLS: (ToolCatalogEntry & GeneratedToolAccess)[] = GENERATED_WORKFLOW_TOOLS;

/** What the level a tool needs reads as, wherever a tool is listed. */
export const REQUIRED_LEVEL_LABEL: Record<RequiredLevel, string> = {
  L1: 'L1',
  L2: 'L2',
  admin: 'admin',
  'admin-role': 'admin with the admin role',
  'chat-refused': 'refused for a chat agent',
  'never-offered': 'never offered to an agent',
};

/** The levels in the order a list or filter shows them. */
export const REQUIRED_LEVEL_ORDER: RequiredLevel[] = ['L1', 'L2', 'admin', 'admin-role', 'chat-refused', 'never-offered'];

/** The level a tool needs, from the offer's probe of it. A tool the catalog has no level for is refused. */
export function requiredLevelOf(actionId: string): RequiredLevel {
  const level: RequiredLevel | undefined = GENERATED_REQUIRED_LEVEL[actionId];
  if (!level) throw new Error(`No required level for ${actionId}: regenerate the tool catalog`);
  return level;
}

/** Per agent kind, null when the code ceiling lets the Tool Manager offer the tool to it, or why it cannot. */
export const KIND_CEILING = GENERATED_KIND_CEILING;

/** The tools outside the access check, the listings filtered per item, the actions set per use (four uses or
 *  three), and the actions just-in-time applies to as shipped. */
export const OUTSIDE_ACCESS_CHECK = new Set(GENERATED_OUTSIDE_ACCESS_CHECK);
export const FILTERED_LISTINGS = new Set(GENERATED_FILTERED_LISTINGS);
export const AWS_FOUR_USE = new Set(GENERATED_AWS_FOUR_USE);
export const AWS_THREE_USE = new Set(GENERATED_AWS_THREE_USE);
export const JIT_SHIPPED = new Set(GENERATED_JIT_SHIPPED);

/** Each AWS use's shipped level (l1, l2 or admin), as aws-use-floor's shipped ladder gives it. */
export const AWS_USE_SHIPPED: Record<string, string> = GENERATED_AWS_USE_SHIPPED;

/** Every action whose access the Tool Manager sets (decision 9), with its shipped operator level. */
export const ACCESS_ACTIONS = GENERATED_ACCESS_ACTIONS;

const APPLIED_LEVEL: Record<string, string> = { l1: 'L1', l2: 'L2', admin: 'admin' };

/** The actions whose floor checks the level even for an admin-role holder: the seven dataset writes
 *  (dataset-write-floor) and the AWS uses (aws-use-floor). Everywhere else a role holder passes whatever the level. */
const LEVEL_BINDS_THE_ROLE = new Set([
  ...GENERATED_DATASET_WRITE_FLOOR, ...GENERATED_AWS_FOUR_USE, ...GENERATED_AWS_THREE_USE,
]);

/**
 * The level an action applies now, as Cedar decides it (ADR-0075): the stored setting where one is saved, else the
 * shipped label. `shippedRole` says the action needs the admin role as shipped (tool-access-floor), which a stored
 * level does not lift while the role setting stays as shipped. Where the role binds, a role holder passes the floor
 * at any level, so the label is the role alone, except where the level binds the role holder too.
 */
export function appliedLevelLabel(
  shippedLabel: string, shippedRole: boolean, stored: StoredToolAccess | undefined,
): string {
  if (!stored) return shippedLabel;
  if (stored.malformed || !stored.access) return 'unreadable setting: refused';
  const { access_required: level, access_admin_role: role } = stored.access;
  const roleBinds = role === 'required' || (role === 'shipped' && shippedRole);
  const levelBinds = LEVEL_BINDS_THE_ROLE.has(stored.action_id);
  if (level === 'shipped') {
    if (roleBinds) {
      if (shippedRole) return shippedLabel;
      return levelBinds ? `${shippedLabel} with the admin role` : 'the admin role';
    }
    // The role is off on an action that ships behind it: only the policies' own level grants remain.
    return shippedRole ? 'as shipped without the admin role' : shippedLabel;
  }
  if (!roleBinds) return APPLIED_LEVEL[level];
  return levelBinds ? `${APPLIED_LEVEL[level]} with the admin role` : 'the admin role';
}
